import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Handle, Position } from '@xyflow/react'
import {
  NodeCard,
  NodeHeader,
  NodeLabel,
  NodeBadge,
  NodeDotRed,
  NodeContent,
  NodeTextPreview,
  NodePlaceholder,
  NodeOptionsList,
  NodeOption,
  NodeOptionNum,
  NodeOptionText,
  NodeActionTag,
} from './styles'
import { isTerminalAction } from './actions'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function StepNode({ data, selected }: { data: any; selected?: boolean }) {
  const { t } = useTranslation()

  const ACTION_LABELS: Record<string, string> = {
    transfer_agent:     t('action_options.transfer_agent'),
    transferToAgent:    t('action_options.transferToAgent'),
    close_conversation: t('action_options.close_conversation'),
    updateFirmware:     t('action_options.updateFirmware'),
  }

  const hasAction  = Boolean(data.action)
  const isTerminal = isTerminalAction(data.action)
  const actionLabel = hasAction
    ? (ACTION_LABELS[data.action] ?? (data.action || t('node.action_label_fallback')))
    : null

  // Visual type: purple when action is set, blue otherwise
  const visualType = hasAction ? 'action' : 'step'

  const msgs       = data.messages || []
  const options    = data.options  || []
  const hasMessage = msgs.length > 0 && msgs[0].trim().length > 0
  const hasOptions = options.length > 0

  return (
    <NodeCard
      $type={visualType}
      $hasOptions={hasOptions}
      $selected={selected}
      $warn={!hasMessage}
      $terminal={isTerminal}
    >
      <Handle type="target" position={Position.Left} className="cf-handle cf-handle-in" style={{ top: '20%' }} />

      <NodeHeader $type={visualType} $hasOptions={hasOptions}>
        {hasAction ? (
          /* Lightning bolt icon for nodes with action */
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
          </svg>
        ) : (
          /* Chat bubble icon for pure message nodes */
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
        <NodeLabel>{data.name || (hasAction ? t('node.action_default_name') : 'Step')}</NodeLabel>
        {msgs.length > 1 && <NodeBadge>{msgs.length}</NodeBadge>}
        {isTerminal && <NodeDotRed title={t('node.terminal_title')} />}
      </NodeHeader>

      <NodeContent>
        {hasMessage ? (
          <NodeTextPreview>{msgs[0]}</NodeTextPreview>
        ) : (
          <NodePlaceholder>{t('node.step_placeholder')}</NodePlaceholder>
        )}

        {hasOptions && (
          <NodeOptionsList>
            {options.map((opt: { label: string }, i: number) => (
              <NodeOption key={i}>
                <NodeOptionNum>{i + 1}</NodeOptionNum>
                <NodeOptionText>{opt.label || '…'}</NodeOptionText>
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`opt-${i}`}
                  className="cf-handle cf-handle-out cf-handle-opt"
                />
              </NodeOption>
            ))}
          </NodeOptionsList>
        )}

        {actionLabel && (
          <NodeActionTag>{actionLabel}</NodeActionTag>
        )}
      </NodeContent>

      {/* Outgoing handles — hidden for terminal actions */}
      {!isTerminal && (
        hasOptions ? (
          <Handle type="source" position={Position.Bottom} id="fallback" className="cf-handle cf-handle-fallback" />
        ) : (
          <>
            <Handle type="source" position={Position.Right} id="next" className="cf-handle cf-handle-out" style={{ top: '80%' }} />
            {data.action === 'updateFirmware' && (
              <>
                <Handle
                  type="source"
                  position={Position.Right}
                  id="up-to-date"
                  className="cf-handle cf-handle-uptodated"
                  style={{ top: '60%' }}
                />
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id="fallback"
                  className="cf-handle cf-handle-fallback"
                />
              </>
            )}
          </>
        )
      )}
    </NodeCard>
  )
}

export default memo(StepNode)
